import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const BugReportButton: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (isLoading || !isAuthenticated) {
    return null;
  }

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 10) {
      enqueueSnackbar(t('bugs.message_required'), { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/v1/bug-reports', {
        message: trimmed,
        page_url: window.location.href,
        language: i18n.language?.split('-')[0],
      });
      enqueueSnackbar(t('bugs.success'), { variant: 'success' });
      setMessage('');
      setOpen(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 429 || detail === 'too_many_reports') {
        enqueueSnackbar(t('bugs.too_many'), { variant: 'warning' });
      } else {
        enqueueSnackbar(t('bugs.error'), { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title={t('bugs.button')}>
        <Fab
          color="primary"
          size="medium"
          onClick={() => setOpen(true)}
          aria-label={t('bugs.button')}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: 1200,
          }}
        >
          <BugReportIcon />
        </Fab>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('bugs.dialog_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bugs.dialog_hint')}
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={4}
            fullWidth
            label={t('bugs.message_label')}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {t('bugs.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BugReportButton;
